'use client'

declare global {
    interface Window {
        snowplow?: (...args: unknown[]) => void
    }
}

/**
 * Converts a camelCase or PascalCase string to snake_case.
 * Matches ui-react's `toSnakeCase` from wanderu-utils.
 */
function toSnakeCase(str: string | undefined): string | undefined {
    if (!str) {
        return str
    }
    return str
        .replace(/([A-Z])/g, '_$1')
        .replace(/^_/, '')
        .toLowerCase()
}

/**
 * Snake-cases all keys in an object (shallow).
 * Matches ui-react's `StructuredEvent.snakeCaseObjectKeys`.
 */
function snakeCaseObjectKeys(obj: Record<string, unknown>): Record<string, unknown> {
    return Object.entries(obj).reduce(
        (acc, [key, value]) => ({ ...acc, [toSnakeCase(key)!]: value }),
        {} as Record<string, unknown>,
    )
}

/**
 * Normalizes property values to match ui-react's StructuredEvent format:
 * - Strings pass through as-is
 * - Objects have their keys snake_cased then get JSON.stringified
 */
function normalizeProperty(
    property: string | Record<string, unknown> | undefined,
): string | undefined {
    if (!property || typeof property === 'string') {
        return property
    }
    return JSON.stringify(snakeCaseObjectKeys(property))
}

/**
 * Fires a Snowplow structured event matching ui-react's StructuredEvent format exactly.
 * Applies snake_case conversion to category, action, and label.
 * Silently drops events if Snowplow is not loaded.
 */
export function trackStructuredEvent({
    category,
    action,
    label,
    property,
    value,
}: {
    category: string
    action: string
    label?: string
    property?: string | Record<string, unknown>
    value?: number
}) {
    if (typeof window === 'undefined' || !window.snowplow) {
        return
    }

    window.snowplow('trackStructEvent', {
        category: toSnakeCase(category),
        action: toSnakeCase(action),
        label: toSnakeCase(label),
        property: normalizeProperty(property),
        value,
    })
}
